import { useCallback, useContext } from "react";
import axios, { AxiosError } from "axios";
import useSWR from "swr";
import { AuthContext } from "../../../context/AuthContext";
import { TradingParametersEndpointEnum } from "../../../enums/endpoints";
import { TRADING_PARAMETERS_API_URL } from "../../../utils/env";
import { pollingInterval } from "../../../constants/pollingInterval";
import { ITradingParameters } from "../../../interfaces/tradingParameters";
import { ToastEnum, ToastStatusEnum, useToast } from "../../toast";

export const useTradingParameters = () => {
  const { queryConfig, tokenId } = useContext(AuthContext);
  const { addToast } = useToast();

  const endpoint = TRADING_PARAMETERS_API_URL(TradingParametersEndpointEnum.GET_TRADING_PARAMETERS);
  const swr = useSWR<ITradingParameters[], AxiosError>(
    tokenId ? [endpoint, tokenId] : undefined,
    async () => {
      const response = await axios.get<{
        success: boolean;
        message: string;
        data: ITradingParameters[];
      }>(endpoint, queryConfig());

      // Sort by asset name
      return response.data.data.sort((a, b) => a.asset.localeCompare(b.asset));
    },
    {
      refreshInterval: pollingInterval[TradingParametersEndpointEnum.GET_TRADING_PARAMETERS] || 30000,
    }
  );

  const updateTradingParameters = useCallback(
    async (payload: Partial<ITradingParameters>) => {
      try {
        await axios.post(
          TRADING_PARAMETERS_API_URL(TradingParametersEndpointEnum.UPDATE_TRADING_PARAMETERS),
          payload,
          {
            headers: {
              Authorization: `Bearer ${tokenId}`,
            },
          }
        );

        addToast(
          {
            type: ToastEnum.SIMPLE,
            header: "Trading Parameters Updated",
            subheader: `Successfully updated parameters for ${payload.asset}`,
            status: ToastStatusEnum.SUCCESS,
          },
          4000
        );

        swr.mutate();
      } catch (error) {
        console.error(error);

        const errorMessage =
          ((error as AxiosError)?.response?.data as any)?.message ||
          "An error occurred";

        addToast(
          {
            type: ToastEnum.SIMPLE,
            header: "Trading Parameters Update Failed",
            subheader: errorMessage,
            status: ToastStatusEnum.ERROR,
          },
          4000
        );
      }
    },
    [addToast, swr, tokenId]
  );

  return {
    ...swr,
    updateTradingParameters,
  };
};
